import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Button,
  Table,
  Tag,
  Tabs,
  message,
  Empty,
  Space,
  Modal,
  Descriptions,
  Tooltip,
} from 'antd';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  CloseCircleOutlined,
  StarFilled,
} from '@ant-design/icons';
import { bookingService } from '../services/api';
import {
  extractList,
  extractObject,
  formatAddress,
  formatBookingDate,
  formatBookingTime,
  formatDateTime,
  formatMoney,
} from '../utils/formatters';
import './CustomerDashboard.css';

const { Title, Paragraph, Text } = Typography;

const statusColors = {
  pending: 'orange',
  confirmed: 'blue',
  in_progress: 'purple',
  completed: 'green',
  cancelled: 'red',
  rejected: 'red',
};

const statusLabels = {
  pending: 'Awaiting confirmation',
  confirmed: 'Confirmed',
  in_progress: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  rejected: 'Rejected',
};

const Bookings = () => {
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [tabKey, setTabKey] = useState('upcoming');

  useEffect(() => {
    fetchBookings();
  }, [tabKey]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const res =
        tabKey === 'upcoming' ? await bookingService.getUpcoming() : await bookingService.getPast();
      setBookings(extractList(res.data));
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (id) => {
    try {
      await bookingService.cancelBooking(id);
      message.success('Booking cancelled');
      fetchBookings();
    } catch (err) {
      message.error(err.response?.data?.message || err.response?.data?.error || 'Failed to cancel');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', render: (id) => <Text strong>#{id}</Text>, width: 70 },
    {
      title: 'Service',
      render: (_, r) => r.product_service?.name || r.service_name || 'Service booking',
      ellipsis: true,
    },
    {
      title: 'Date',
      render: (_, r) => formatBookingDate(r.booking_date),
      width: 120,
    },
    {
      title: 'Time',
      render: (_, r) => formatBookingTime(r.booking_time),
      width: 100,
    },
    {
      title: 'Provider',
      render: (_, r) =>
        r.provider?.name || r.provider?.full_name || r.provider_name || 'To be assigned',
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s) => (
        <Tag color={statusColors[s] || 'default'}>{statusLabels[s] || (s || '').toUpperCase()}</Tag>
      ),
      width: 140,
    },
    {
      title: 'Amount',
      render: (_, r) => <Text strong>{formatMoney(r.total_amount ?? r.total_price)}</Text>,
      width: 110,
    },
    {
      title: 'Loyalty',
      render: (_, r) =>
        r.status === 'completed' ? (
          r.loyalty_points_earned > 0 ? (
            <Tag color="gold" icon={<StarFilled />}>
              +{r.loyalty_points_earned}
            </Tag>
          ) : (
            <Tooltip title="This booking did not meet the minimum spend for points">
              <Text type="secondary">—</Text>
            </Tooltip>
          )
        ) : (
          <Text type="secondary">On completion</Text>
        ),
      width: 120,
    },
    {
      title: 'Actions',
      width: 150,
      render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => openDetails(r)}>
            Details
          </Button>
          {['pending', 'confirmed'].includes(r.status) && (
            <Button
              size="small"
              danger
              icon={<CloseCircleOutlined />}
              onClick={() => {
                Modal.confirm({ title: 'Cancel booking?', onOk: () => handleCancel(r.id) });
              }}
            >
              Cancel
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // Detail data comes from the list row, but a completed booking's points and
  // completion date are only authoritative on the detail endpoint.
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const openDetails = async (row) => {
    setDetail(row);
    setDetailLoading(true);
    try {
      const res = await bookingService.getBookingDetails(row.id);
      const payload = extractObject(res.data);
      if (payload) setDetail(payload);
    } catch (err) {
      console.error('Failed to load booking details:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetails = () => {
    setDetail(null);
    setDetailLoading(false);
  };

  return (
    <div className="customer-page" style={{ padding: 0 }}>
      <div className="customer-page-header">
        <Title level={2}>My Bookings</Title>
        <Paragraph>Manage your service bookings and appointments.</Paragraph>
      </div>

      <Card className="customer-card">
        <Tabs
          activeKey={tabKey}
          onChange={setTabKey}
          items={[
            {
              key: 'upcoming',
              label: (
                <span>
                  <CalendarOutlined /> Upcoming
                </span>
              ),
            },
            {
              key: 'past',
              label: (
                <span>
                  <ClockCircleOutlined /> Past
                </span>
              ),
            },
          ]}
        />
        <Table
          columns={columns}
          dataSource={bookings}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10, size: 'small' }}
          scroll={{ x: 1000 }}
          locale={{ emptyText: <Empty description={`No ${tabKey} bookings found`} /> }}
        />
      </Card>

      <Modal
        title={detail ? `Booking #${detail.id}` : 'Booking'}
        open={!!detail}
        onCancel={closeDetails}
        footer={null}
        width={520}
        loading={detailLoading}
      >
        {detail && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Service">
              {detail.product_service?.name || detail.service_name || 'Service booking'}
            </Descriptions.Item>
            <Descriptions.Item label="Provider">
              {detail.provider?.name ||
                detail.provider?.full_name ||
                detail.provider_name ||
                'To be assigned'}
            </Descriptions.Item>
            {detail.provider?.phone && (
              <Descriptions.Item label="Provider contact">
                {detail.provider.phone}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Booked for">
              {formatBookingDate(detail.booking_date)}
            </Descriptions.Item>
            <Descriptions.Item label="Time">
              {formatBookingTime(detail.booking_time)}
            </Descriptions.Item>
            {detail.started_at && (
              <Descriptions.Item label="Started">
                {formatDateTime(detail.started_at)}
              </Descriptions.Item>
            )}
            {detail.completed_at && (
              <Descriptions.Item label="Completed">
                {formatDateTime(detail.completed_at)}
              </Descriptions.Item>
            )}
            {detail.cancelled_at && (
              <Descriptions.Item label="Cancelled">
                {formatDateTime(detail.cancelled_at)}
              </Descriptions.Item>
            )}
            {detail.status === 'completed' && (
              <Descriptions.Item label="Loyalty points">
                {detail.loyalty_points_earned > 0 ? (
                  <Tag color="gold" icon={<StarFilled />}>
                    {detail.loyalty_points_earned} earned
                  </Tag>
                ) : (
                  <Text type="secondary">No points earned for this booking</Text>
                )}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Status">
              <Tag color={statusColors[detail.status]}>
                {statusLabels[detail.status] || (detail.status || '').toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Total">
              <Text strong>{formatMoney(detail.total_amount ?? detail.total_price)}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Payment">
              {detail.payment_status || detail.order?.payment_status || 'Not paid yet'}
              {detail.payment_method ? ` · ${String(detail.payment_method).toUpperCase()}` : ''}
            </Descriptions.Item>
            {detail.order && (
              <Descriptions.Item label="Order">
                #{detail.order.id} · {detail.order.order_status}
              </Descriptions.Item>
            )}
            <Descriptions.Item
              label={
                <span>
                  <EnvironmentOutlined /> Location
                </span>
              }
            >
              {detail.address ? (
                formatAddress(detail)
              ) : (
                <Text type="secondary">No address recorded</Text>
              )}
            </Descriptions.Item>
            {detail.notes && <Descriptions.Item label="Notes">{detail.notes}</Descriptions.Item>}
            {detail.special_requirements && (
              <Descriptions.Item label="Requirements">
                {detail.special_requirements}
              </Descriptions.Item>
            )}
            {detail.cancellation_reason && (
              <Descriptions.Item label="Cancellation reason">
                {detail.cancellation_reason}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default Bookings;
